import React, { useState } from "react";
import { Check, ChevronRight, ShieldAlert, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { AiApprovalDecision, AiPendingApproval } from "../../../models/AiAssistant";

interface AiApprovalCardProps {
	approvals: AiPendingApproval[];
	onRespond: (decisions: AiApprovalDecision[]) => void;
	isSubmitting?: boolean;
	className?: string;
}

/**
 * The one card per turn that lists every write the assistant wants to make.
 * Rows are checked by default; "Godkänn" executes the checked ones and
 * denies the rest, "Avvisa alla" denies everything. Nothing is written
 * server-side until this card is answered.
 */
const AiApprovalCard: React.FC<AiApprovalCardProps> = ({
	approvals,
	onRespond,
	isSubmitting,
	className,
}) => {
	const [selected, setSelected] = useState<Set<string>>(
		() => new Set(approvals.map((approval) => approval.approvalId))
	);
	const [expanded, setExpanded] = useState<Set<string>>(new Set());

	const toggle = (approvalId: string) =>
		setSelected((current) => {
			const next = new Set(current);
			if (next.has(approvalId)) next.delete(approvalId);
			else next.add(approvalId);
			return next;
		});

	const toggleExpanded = (approvalId: string) =>
		setExpanded((current) => {
			const next = new Set(current);
			if (next.has(approvalId)) next.delete(approvalId);
			else next.add(approvalId);
			return next;
		});

	const approve = () =>
		onRespond(
			approvals.map((approval) => ({
				approvalId: approval.approvalId,
				approved: selected.has(approval.approvalId),
				reason: selected.has(approval.approvalId) ? undefined : "Användaren avvisade ändringen.",
			}))
		);

	const denyAll = () =>
		onRespond(
			approvals.map((approval) => ({
				approvalId: approval.approvalId,
				approved: false,
				reason: "Användaren avvisade ändringen.",
			}))
		);

	const selectedCount = selected.size;

	return (
		<div
			className={cn(
				"rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm shadow-sm",
				isSubmitting && "opacity-60",
				className
			)}
		>
			<div className="mb-2 flex items-center gap-2 font-semibold text-amber-900">
				<ShieldAlert className="h-4 w-4" />
				{approvals.length === 1
					? "Assistenten vill göra en ändring"
					: `Assistenten vill göra ${approvals.length} ändringar`}
			</div>

			<ul className="space-y-1">
				{approvals.map((approval) => {
					const isOpen = expanded.has(approval.approvalId);
					return (
						<li key={approval.approvalId} className="rounded-md bg-white/70 px-2 py-1.5">
							<div className="flex items-start gap-2">
								<Checkbox
									id={`approval-${approval.approvalId}`}
									checked={selected.has(approval.approvalId)}
									onCheckedChange={() => toggle(approval.approvalId)}
									disabled={isSubmitting}
									className="mt-0.5"
								/>
								<label
									htmlFor={`approval-${approval.approvalId}`}
									className="flex-1 cursor-pointer leading-snug text-foreground"
								>
									{approval.summary}
								</label>
								<button
									type="button"
									onClick={() => toggleExpanded(approval.approvalId)}
									className="inline-flex items-center gap-0.5 rounded px-1 font-mono text-[11px] text-muted-foreground hover:bg-neutral-100"
									title="Visa exakt vad som skickas"
								>
									<ChevronRight
										className={cn("h-3 w-3 transition-transform", isOpen && "rotate-90")}
									/>
									{approval.toolName}
								</button>
							</div>
							{isOpen && (
								<pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all rounded bg-neutral-50 p-2 font-mono text-[11px] text-muted-foreground">
									{JSON.stringify(approval.input, null, 2)}
								</pre>
							)}
						</li>
					);
				})}
			</ul>

			<div className="mt-3 flex justify-end gap-2">
				<button
					type="button"
					onClick={denyAll}
					disabled={isSubmitting}
					className="inline-flex h-8 items-center gap-1 rounded-md border border-gray-300 bg-white px-3 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
				>
					<X className="h-3.5 w-3.5" /> Avvisa alla
				</button>
				<button
					type="button"
					onClick={approve}
					disabled={isSubmitting || selectedCount === 0}
					className="inline-flex h-8 items-center gap-1 rounded-md bg-brand px-3 text-xs font-medium text-brand-foreground hover:bg-brand/90 disabled:opacity-50"
				>
					<Check className="h-3.5 w-3.5" />
					{selectedCount === approvals.length
						? "Godkänn"
						: `Godkänn ${selectedCount} av ${approvals.length}`}
				</button>
			</div>
		</div>
	);
};

export default AiApprovalCard;
